import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Popup } from "@progress/kendo-react-popup";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { setExpanded } from "../../../actions/taskSidebar";
import { icon, label, number, quote } from "../../../config";
import { formatDate } from "../../../helper/common";
import { useCloseOnClickOutsideLocal } from "../../../utils/common";
import { FLOW_ELEMENTS_SECTION_ID } from "../../Tasks/tasks.constants";
import { getCurrentActiveProject } from "../sidebar.common";

/**
 * DetailedProjectTrails
 * @author Shivam Mishra
 */
const DetailedProjectTrails = ({ anchor, setShowPopup, flowElementsSectionRef }) => {
  const dispatch = useDispatch();
  const { flowElements, activeMemberVariables, expanded } = useSelector((state) => state.taskSidebar);
  const { allProjectsList } = useSelector((state) => state.tasks.defaultDetails);
  const projectTrailRef = React.useRef();
  const { currentProject: currentActiveProject } = getCurrentActiveProject(flowElements);

  /**
   * Navigate to Flow Elements section – same flow as SidepanelQuickActions: expand, then after 600ms scroll into view and close popup.
   * Uses flowElementsSectionRef (from parent) instead of document.getElementById.
   */
  const handleEditRoute = async () => {
    await dispatch(setExpanded([...(expanded || []), FLOW_ELEMENTS_SECTION_ID]));
    setTimeout(() => {
      const target = flowElementsSectionRef?.current ?? document.getElementById(FLOW_ELEMENTS_SECTION_ID);
      target?.scrollIntoView();
      setShowPopup?.(false);
    }, number.SIX_HUNDRED);
  };

  /**
   * approval icon for breadcrumbs
   * @param {index} integer
   * @return {jsx}
   */
  function getApprovalIcon(index) {
    const currentActiveIndex = flowElements.indexOf(currentActiveProject);

    if (index > currentActiveIndex) {
      return <span className='approval-reached d-flex align-items-center justify-content-center'>{icon.APPROVAL_ICON}</span>;
    } else {
      return <span className={`approval-${index === currentActiveIndex ? "taken" : "moved"}`}>{icon.APPORVAL_APPROVED}</span>;
    }
  }

  /**
   * List item of Project trails
   * @param {props} object
   * @return {jsx}
   */
  function RenderTrails(props) {
    const { assignmentProjectId, index, id, isApproval, workflowIcon, name } = props;
    const currentProject = allProjectsList?.find((project) => project?.ProjectId == assignmentProjectId);
    const ProjectName = props?.flowElementType === number.TWO ? name : currentProject?.ProjectName;
    const isActiveProject = currentActiveProject?.id == id;
    const isLastElement = flowElements.length - number.ONE === index;
    const isCompletedProject = props?.isCompleted && !isActiveProject;
    const isEscalationEnabled = parseInt(props?.isEscalationEnabled);
    const hasActiveEscalation = parseInt(props?.isTaskEscalate || props?.isNotificationEscalate);
    const shouldShowEscalation = hasActiveEscalation || isEscalationEnabled;

    const iconClasses = [
      "project-trails-dot",
      "d-flex",
      "align-items-center",
      "justify-content-center",
      isActiveProject ? "project-trails-active-project" : "",
      isCompletedProject ? "disable-icon" : "project-trails-active",
    ].join(" ");

    /**
     * returns title for project Trails
     * @param {id ,projectName , index} integer , string , integer
     * @return {jsx}
     */
    const getTitle = (props) => {
      const { completedBy, completedOn } = props;
      if (isActiveProject) {
        return (
          <div>
            <div className='project-trails-name'>{ProjectName}</div>
            <div>
              <span className='project-trails-username font-weight-normal d-flex justify-content-between align-items-center'>
                Current Stage
                <span className='mr-1 status-info-icon'>
                  {!!(props?.flowElementType === number.TWO) && <span title={label.DYNAMIC_MEMBER}>{label.DYNAMIC}</span>}
                  {!!shouldShowEscalation && (
                    <span 
                      className={hasActiveEscalation ? 'project-trails-escalation-active' : 'project-trails-escalation-enabled'}
                      title={hasActiveEscalation ? label.TASK_ESCALATED : label.TASK_ESCALATION_ENABLED}>
                      {icon.TASK_ESCALATION}
                    </span>
                  )}
                  {!!isApproval && <span title={label.APPROVAL}>{icon.APPROVAL_ICON}</span>}
                  {!!isLastElement && <span title={quote.LAST_FLOW_ELEMENT}>{icon.FLAG}</span>}
                </span>
              </span>
            </div>
          </div>
        );
      }
      if (!isCompletedProject) {
        return (
          <div>
            <div className='project-trails-name'>{ProjectName}</div>
            <div>
              <span className='project-trails-username font-weight-normal d-flex justify-content-between align-items-center'>
                {props?.flowDetail}
                {
                  <span className='mr-1 status-info-icon'>
                    {!!(props?.flowElementType === number.TWO) && <span title={label.DYNAMIC_MEMBER}>{label.DYNAMIC}</span>}
                    {!!isApproval && <span title={label.APPROVAL}>{icon.APPROVAL_ICON}</span>}
                    {!!shouldShowEscalation && (
                      <span 
                        className={hasActiveEscalation ? 'project-trails-escalation-active' : 'project-trails-escalation-enabled'}
                        title={hasActiveEscalation ? label.TASK_ESCALATED : label.TASK_ESCALATION_ENABLED}>
                        {icon.TASK_ESCALATION}
                      </span>
                    )}
                    {!!isLastElement && <span title='Last Flow Element'>{icon.FLAG}</span>}
                  </span>
                }
              </span>
            </div>
          </div>
        );
      }
      return (
        <div>
          <div className='project-trails-name'>{ProjectName}</div>
          <div>
            <span className='project-trails-username font-weight-normal d-flex justify-content-between align-items-center'>
              {completedBy && completedOn ? (
                <span>
                  <span className='font-weight-normal'>{completedBy}</span>
                  <span className='project-trails-date font-weight-normal ml-1'>({formatDate(completedOn)})</span>
                </span>
              ) : (
                <span>{props?.flowDetail || ''}</span>
              )}
              <span className='mr-1 status-info-icon'>
                {!!(props?.flowElementType === number.TWO) && <span title={label.DYNAMIC_MEMBER}>{label.DYNAMIC}</span>}
                {!!isApproval && <span title={label.APPROVAL}>{icon.APPROVAL_ICON}</span>}
                {!!shouldShowEscalation && (
                  <span 
                    className={hasActiveEscalation ? 'project-trails-escalation-active' : 'project-trails-escalation-enabled'}
                    title={hasActiveEscalation ? label.TASK_ESCALATED : label.TASK_ESCALATION_ENABLED}>
                    {icon.TASK_ESCALATION}
                  </span>
                )}  
                {!!isLastElement && <span title={quote.LAST_FLOW_ELEMENT}>{icon.FLAG}</span>}
              </span>
            </span>
          </div>
        </div>
      );
    };

    return (
      <Tooltip anchorElement='target' parentTitle={true} position='bottom'>
        <div
          className={`project-trails-phase d-flex position-relative align-items-center ${
            isActiveProject ? "active-bg" : isCompletedProject ? "disable-project-trails" : ""
          }`}
          id={`project-trails-phase-${index}`}>
          <span className={iconClasses}>{props?.flowElementType === number.TWO ? icon.DYNAMIC : icon[workflowIcon]}</span>
          <span className='project-trails-text w-100' data-toggle='tooltip' data-placement='bottom'>
            {getTitle(props)}
          </span>
        </div>
      </Tooltip>
    );
  }

  useCloseOnClickOutsideLocal(projectTrailRef, "project-trail", setShowPopup);
  return (
    <>
      <Popup
        popupClass={"project-trails-wrapper border-0 bg-transparent position-relative"}
        show={true}
        anchor={anchor.current}
        animate={false}
        collision={{
          horizontal: "fit",
          vertical: "flip",
        }}
        className='project-trails-container'>
        <div ref={projectTrailRef} className='project-trails p-3 d-inline-block text-left overflow-auto mt-1'>
          <div className='mb-2 d-flex align-items-center justify-content-between'>
            <span className='font-weight-semi-bold'>Route</span>
            <Tooltip anchorElement='target' parentTitle={true} position='bottom'>
              <span
                className='cursor-pointer project-trails-edit-icon'
                title='Edit Route'
                onClick={handleEditRoute}
                onKeyDown={(e) => e.key === "Enter" && handleEditRoute()}
                role='button'>
                {icon.EDIT_ICON_2}
              </span>
            </Tooltip>
          </div>
          {flowElements?.map((item, index) => {
            if (item.flowElementType === number.TWO) {
              item.name = activeMemberVariables?.find((variable) => variable._id === item.variableId)?.name;
              return <RenderTrails key={index} {...item} index={index} />;
            }
            return <RenderTrails key={index} {...item} index={index} />;
          })}
        </div>
      </Popup>
    </>
  );
};
export default React.memo(DetailedProjectTrails);
